import { useParams } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Stack,
  Zoom,
} from "@mui/material";
import {
  COLOR_BLACK,
  COLOR_LESS_BLACK,
  COLOR_LIGHT_GRAY,
  COLOR_MAIN,
  COLOR_VERY_LIGHT_GRAY,
  COLOR_WHITE,
} from "../../../../data/colors/Colors";
import {
  Attachment,
  CheckCircle,
  CloudUploadRounded,
  Delete,
  EditRounded,
  Save,
} from "@mui/icons-material";
import { CustomImageView } from "../../../component/CustomImageView";
import useWindowDimensions from "../../../../core/utils/UseWindowDimensions";
import {
  STRINGS_ARE_YOU_SURE_YOU_WANT_TO_DELETE_THE_CATEGORY,
  STRINGS_CATEGORY,
  STRINGS_CREATE,
  STRINGS_DELETE,
  STRINGS_DELETE_CATEGORY,
  STRINGS_EDIT_TITLE,
  STRINGS_SAVE,
  STRINGS_TITLE,
} from "../../../../data/strings/Strings";
import { useEffect, useRef, useState } from "react";
import AutoHeight from "../../../component/AutoHeightView";
import { CustomTextField } from "../../../component/CustomTextField";
import { TitleView } from "../../../component/TitleView";
import { CustomTextView } from "../../../component/CustomTextView";
import { HeaderView } from "../../../component/HeaderView";
import { CustomTitledView } from "../../../component/CustomTitledView";
import { ROUTE_NEW } from "../../../../data/constant/RouteConstants";
import { CategoryDetailsViewModel } from "./CategoryDetailsViewModel";
import { FilePicker } from "../../../component/FilePicker";
import {
  provideCreateCategoryUseCase,
  provideDeleteCategoryUseCase,
  provideGetCategoryUseCase,
  provideUpdateCategoryUseCase,
  provideUploadFileUseCase,
} from "../../../di/UseCasesModule";
import { CustomAlertDialog } from "../../../component/CustomAlertDialog";
import {
  getDominantColorOfImage,
  hasEnglishLetters,
} from "../../../../core/utils/Utils";
import Marquee from "react-fast-marquee";
import { LoadingView } from "../../../component/LoadingView";

export function CategoryDetailsView() {
  const { id } = useParams();
  const viewModel = CategoryDetailsViewModel(
    provideGetCategoryUseCase(),
    provideCreateCategoryUseCase(),
    provideDeleteCategoryUseCase(),
    provideUpdateCategoryUseCase(),
    provideUploadFileUseCase()
  );
  const { width } = useWindowDimensions();
  const isNewPage = id === ROUTE_NEW;
  const [titleEditable, setTitleEditable] = useState(isNewPage);
  const [titleInArabicEditable, setTitleInArabicEditable] = useState(isNewPage);
  const [showDeleteAlertDialog, setShowDeleteAlertDialog] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    if (!isNewPage && id) {
      viewModel.fetchCategory(id);
    }
  }, []);

  const ref = useRef(null);
  const [imageWidth, setImageWidth] = useState();
  useEffect(() => {
    setImageWidth(ref.current ? (ref.current as any).offsetWidth : 0);
  });

  return (
    <Box>
      <CustomAlertDialog
        title={STRINGS_DELETE_CATEGORY}
        description={STRINGS_ARE_YOU_SURE_YOU_WANT_TO_DELETE_THE_CATEGORY}
        open={showDeleteAlertDialog}
        handleNegativeButtonClick={() => setShowDeleteAlertDialog(false)}
        handlePositiveButtonClick={() => {
          viewModel.deleteCategory();
          setShowDeleteAlertDialog(false);
        }}
      />
      <Stack>
        {viewModel.isLoading ? (
          <LoadingView />
        ) : (
          <>
            <Stack>
              <HeaderView
                isLayoutFullWidthInSmallerScreens={!isNewPage}
                title={STRINGS_CATEGORY}
                alignItems="start"
                titleLayout={
                  <>
                    {viewModel.category?._id && (
                      <Marquee style={{ width: 100 }} delay={2} pauseOnHover>
                        <CustomTextView
                          text={`#${viewModel.category._id}`}
                          fontWeight="300"
                          variant="h6"
                          textColor={COLOR_LIGHT_GRAY}
                        />
                      </Marquee>
                    )}
                  </>
                }
                layout={
                  <Stack
                    alignItems={"center"}
                    justifyContent={!isNewPage && width < 900 ? "start" : "end"}
                    spacing={1}
                    width={"100%"}
                  >
                    <Stack
                      direction={"row"}
                      alignItems={"center"}
                      justifyContent={
                        !isNewPage && width < 900 ? "start" : "end"
                      }
                      spacing={1}
                      width={"100%"}
                    >
                      {!isNewPage && (
                        <Button
                          onClick={() => setShowDeleteAlertDialog(true)}
                          variant="outlined"
                          fullWidth
                          sx={{
                            borderRadius: 22,
                            borderColor: "red",
                            "&:hover": {
                              borderColor: "red",
                            },
                          }}
                        >
                          <Delete fontSize="small" sx={{ color: "red" }} />
                          <CustomTextView
                            text={STRINGS_DELETE}
                            fontWeight="bold"
                            textColor={"red"}
                            fontSize={16}
                            props={{ ml: 1, textTransform: "none" }}
                          />
                        </Button>
                      )}
                      <Button
                        onClick={
                          isNewPage
                            ? viewModel.createCategory
                            : viewModel.saveCategory
                        }
                        variant="outlined"
                        fullWidth={!isNewPage}
                        sx={{
                          borderRadius: 22,
                          borderColor: COLOR_BLACK,
                          "&:hover": {
                            borderColor: COLOR_BLACK,
                          },
                        }}
                      >
                        <Save fontSize="small" sx={{ color: COLOR_BLACK }} />
                        <CustomTextView
                          text={isNewPage ? STRINGS_CREATE : STRINGS_SAVE}
                          fontWeight="bold"
                          fontSize={16}
                          props={{ ml: 1, textTransform: "none" }}
                          textColor={COLOR_BLACK}
                        />
                      </Button>
                    </Stack>
                  </Stack>
                }
              />

              <Box p={3}>
                {/* <TitleView title={"Details"} size="h5" /> */}
                <Grid container spacing={2} mt={2}>
                  <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 8 }}>
                    <Zoom in={viewModel.category != undefined}>
                      <Box height={400} ref={ref} position={"relative"}>
                        {viewModel.category?.imageUrl &&
                          viewModel.category.imageUrl.length > 0 &&
                          imageLoaded && (
                            <Box position={"absolute"} zIndex={3}>
                              <Stack
                                width={"100%"}
                                direction={"row"}
                                alignItems={"center"}
                                justifyContent={"space-between"}
                                padding={2}
                                zIndex={2}
                              >
                                <Button
                                  sx={{
                                    border: "solid 1px gray",
                                    borderRadius: 12,
                                    p: 1,
                                  }}
                                >
                                  <FilePicker
                                    handleFilePicked={viewModel.setImageUrl}
                                    layout={
                                      <Stack
                                        direction={"row"}
                                        alignItems={"center"}
                                        spacing={1}
                                      >
                                        <Attachment
                                          fontSize="small"
                                          sx={{ color: COLOR_LIGHT_GRAY }}
                                        />
                                      </Stack>
                                    }
                                    acceptType="image/*"
                                  />
                                </Button>
                              </Stack>
                            </Box>
                          )}

                        <Box
                          position={"absolute"}
                          zIndex={2}
                          width={imageWidth}
                          alignItems={"center"}
                          justifyContent={"center"}
                          display={"flex"}
                        >
                          {viewModel.category?.imageUrl &&
                          viewModel.category.imageUrl.length > 0 ? (
                            <Stack width={"100%"}>
                              <CustomImageView
                                width={"100%"}
                                height={400}
                                onLoaded={() => {
                                  setImageLoaded(true);
                                }}
                                src={viewModel.category.imageUrl}
                                scale="contain"
                                props={{
                                  borderRadius: 8,
                                  background: "transparent",
                                  border: imageLoaded
                                    ? `solid 1px ${COLOR_LIGHT_GRAY}`
                                    : undefined,
                                }}
                              />
                            </Stack>
                          ) : (
                            <Box
                              sx={{
                                width: "100%",
                                height: 400,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                border: "solid 1px " + COLOR_LIGHT_GRAY,
                                borderRadius: 8,
                              }}
                            >
                              <FilePicker
                                handleFilePicked={viewModel.setImageUrl}
                                layout={
                                  <CloudUploadRounded
                                    sx={{
                                      color: COLOR_MAIN,
                                      width: 200,
                                      height: 200,
                                    }}
                                  />
                                }
                                acceptType="image/*"
                              />
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Zoom>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 4 }}>
                    <CustomTitledView
                      title={STRINGS_TITLE}
                      editIcon={
                        titleEditable ? (
                          <CheckCircle
                            fontSize="medium"
                            sx={{ color: COLOR_BLACK }}
                          />
                        ) : (
                          <EditRounded
                            fontSize="medium"
                            sx={{ color: COLOR_BLACK }}
                          />
                        )
                      }
                      onEditButtonClicked={() => {
                        if (
                          viewModel.category?.title &&
                          viewModel.category.title.length === 0
                        )
                          return;
                        setTitleEditable(!titleEditable);
                      }}
                      layout={
                        <Stack>
                          {titleEditable ? (
                            <CustomTextField
                              value={viewModel.category?.title}
                              fontSize={30}
                              textColor={COLOR_BLACK}
                              fontWeight="bold"
                              multiLine
                              backgroundColor="transparent"
                              placeholder={STRINGS_EDIT_TITLE}
                              variant="standard"
                              onTextChange={viewModel.setTitle}
                              sxProps={{
                                p: 2,
                                direction: "ltr",
                              }}
                            />
                          ) : (
                            <CustomTextView
                              text={viewModel.category?.title}
                              fontWeight="bold"
                              fontSize={30}
                              textColor={COLOR_BLACK}
                              props={{
                                p: 2,
                                direction: "ltr",
                              }}
                            />
                          )}
                        </Stack>
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 4 }}>
                    <CustomTitledView
                      title={"Title in Arabic"}
                      editIcon={
                        titleEditable ? (
                          <CheckCircle
                            fontSize="medium"
                            sx={{ color: COLOR_BLACK }}
                          />
                        ) : (
                          <EditRounded
                            fontSize="medium"
                            sx={{ color: COLOR_BLACK }}
                          />
                        )
                      }
                      onEditButtonClicked={() => {
                        if (
                          viewModel.category?.titleInArabic &&
                          viewModel.category.titleInArabic.length === 0
                        )
                          return;
                        setTitleInArabicEditable(!titleInArabicEditable);
                      }}
                      layout={
                        <Stack>
                          {titleInArabicEditable ? (
                            <CustomTextField
                              value={viewModel.category?.titleInArabic}
                              fontSize={30}
                              textColor={COLOR_BLACK}
                              fontWeight="bold"
                              multiLine
                              backgroundColor="transparent"
                              placeholder={STRINGS_EDIT_TITLE}
                              variant="standard"
                              onTextChange={viewModel.setTitleInArabic}
                              sxProps={{
                                p: 2,
                                direction: "rtl",
                              }}
                            />
                          ) : (
                            <CustomTextView
                              text={viewModel.category?.titleInArabic}
                              fontWeight="bold"
                              fontSize={30}
                              textColor={COLOR_BLACK}
                              props={{
                                p: 2,
                                direction: "rtl",
                              }}
                            />
                          )}
                        </Stack>
                      }
                    />
                  </Grid>
                </Grid>
              </Box>
            </Stack>
          </>
        )}
      </Stack>
    </Box>
  );
}
